import {
    getStorageAdapater,
    StorageEntry,
    StorageFolder,
    StorageTarget
} from '@assets/firebase/StorageAdapater';
import { observer } from 'mobx-react';
import {
    forwardRef,
    ReactNode,
    useEffect,
    useImperativeHandle,
    useState
} from 'react';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';

import { Chip, Stack, Typography } from '@mui/material';
import type { TreeItemProps } from '@mui/x-tree-view/TreeItem';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { useTreeItemModel } from '@mui/x-tree-view/hooks';

import ArticleIcon from '@mui/icons-material/Article';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import FolderRounded from '@mui/icons-material/FolderRounded';
import { useTreeItem } from '@mui/x-tree-view/useTreeItem';
import { StorageUpload } from '../StorageUpload/StorageUpload';
import type { TreeViewBaseItem } from '@mui/x-tree-view/models';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import { IndustryTag } from '../IndustryTagSelect/IndustryTagSelect';
import { dbKnowledgeFileSanitizer } from '@assets/firebase/FirebaseAdapater';
import { KnowledgeFileType } from '@assets/model/Store';
import { Button } from 'react-bootstrap';
import { getStorage } from 'firebase/storage';
import { assert } from 'console';

type FileOrFolder = 'file' | 'folder';
type RagStatus = 'completed' | 'not-started';
export interface TreeEntry extends TreeViewBaseItem {
    icon?: ReactNode;
    type: FileOrFolder;
    path: string;
    tags: IndustryTag[];
    ragStatus: RagStatus;
}

interface KnowledgeFileTreeProps {
    folderContent: StorageFolder;
    knowledgeFilesDB: KnowledgeFileType;
    refreshFolder: () => void;
}

export interface KnowledgeFileTreeAPI {
    getSelectedItems: () => string[];
    getTreeItems: () => TreeEntry[];
}

export const KnowledgeFileTree = observer(
    forwardRef<KnowledgeFileTreeAPI, KnowledgeFileTreeProps>(
        (props: KnowledgeFileTreeProps, ref) => {
            const { folderContent } = props;
            const ROOT_FOLDER = 'knowledge';
            const [expandedTree, setExpandedTree] = useState<string[]>([
                ROOT_FOLDER
            ]);
            const [treeItems, setFolderTreeItems] = useState<TreeEntry[]>([]);
            const [selectedIds, setSelectedIds] = useState<string[]>([]);
            const { refreshFolder } = props;
            useImperativeHandle(
                ref,
                () => ({
                    getSelectedItems: () => {
                        return selectedIds;
                    },
                    getTreeItems: () => {
                        return treeItems;
                    }
                }),
                [selectedIds, treeItems]
            );

            const onCreateFolder = (storageTarget: StorageTarget) => {
                const newFolder = prompt('Enter folder name', 'New Folder');
                const doWork = async () => {
                    const storage = getStorageAdapater();
                    await storage.createFolder(storageTarget, newFolder);
                    refreshFolder();
                };
                if (newFolder) {
                    doWork();
                }
            };

            const onDeleteFolder = (
                storageTarget: Extract<
                    StorageTarget,
                    { type: 'knowledge_base' }
                >
            ) => {
                const areYouSure = confirm(
                    `Are you sure you want to delete this folder? \r\n *** ${storageTarget.folder} ***`
                );
                const doWork = async () => {
                    const storage = getStorageAdapater();
                    await storage.deleteFolder(storageTarget);
                    refreshFolder();
                };
                if (areYouSure) {
                    doWork();
                }
            };

            const onDeleteFile = (storagePath: string) => {
                const areYouSure = confirm(
                    `Are you sure you want to delete this file?: \r\n *** ${storagePath} ***`
                );

                const doWork = async () => {
                    const storage = getStorageAdapater();
                    await storage.deleteKnowledgeFile(storagePath);
                    refreshFolder();
                };

                if (areYouSure) {
                    doWork();
                }
            };
            const handleToggleTree = (
                event: React.SyntheticEvent,
                itemIds: string[]
            ) => {
                if (itemIds.indexOf(ROOT_FOLDER) === -1) {
                    itemIds.push(ROOT_FOLDER);
                }

                setExpandedTree(itemIds);
            };
            const getTagsFolder = (folder: StorageFolder): IndustryTag[] => {
                const tags: IndustryTag[] = [];
                folder.files.forEach(file => {
                    const fileTags = getTags(file.path);
                    fileTags.forEach(tag => {
                        if (!tags.find(t => t.value === tag.value)) {
                            tags.push(tag);
                        }
                    });
                });

                folder.folders.forEach(subFolder => {
                    const subTags = getTagsFolder(subFolder);
                    subTags.forEach(tag => {
                        if (!tags.find(t => t.value === tag.value)) {
                            tags.push(tag);
                        }
                    });
                });

                return tags;
            };
            const getRagStatus = (path: string): RagStatus => {
                const sanitized = dbKnowledgeFileSanitizer(path);
                const entry = props.knowledgeFilesDB[sanitized];
                if (entry && entry.ragStatus) {
                    return entry.ragStatus as RagStatus;
                } else {
                    return 'not-started';
                }
            };

            const getTags = (path: string): IndustryTag[] => {
                const sanitized = dbKnowledgeFileSanitizer(path);
                const entry = props.knowledgeFilesDB[sanitized];
                if (entry) {
                    return entry.tags ?? [];
                } else {
                    return [];
                }
            };

            const makeFileTree = (folder: StorageFolder): TreeEntry => {
                const buildFile = (item: StorageEntry): TreeEntry => {
                    const tags = getTags(item.path);
                    const ragStatus = getRagStatus(item.path);
                    return {
                        id: item.path,
                        label: item.fileName,
                        path: item.path,
                        type: 'file',
                        tags: tags,
                        ragStatus: ragStatus
                    };
                };
                const buildFolder = (folder: StorageFolder): TreeEntry => {
                    const files = folder.files.map(file => buildFile(file));
                    const tags = getTagsFolder(folder);
                    const children = folder.folders.map(subFolder =>
                        buildFolder(subFolder)
                    );
                    return {
                        id: folder.path,
                        path: folder.path,
                        label: folder.name,
                        children: children.concat(files),
                        type: 'folder',
                        tags: tags
                    };
                };
                return buildFolder(folder);
            };

            useEffect(() => {
                if (folderContent) {
                    const treeItems = makeFileTree(folderContent);
                    setFolderTreeItems([treeItems]);
                }
            }, [folderContent, props.knowledgeFilesDB]);

            interface CustomIconProps {
                children: string;
                className: string;
                isOpen: boolean;
                type: FileOrFolder;
            }

            function CustomIcon(props: CustomIconProps) {
                const { isOpen, type, className } = props;
                return (
                    <div className={className}>
                        {type === 'folder' ? (
                            isOpen ? (
                                <FolderOpenIcon />
                            ) : (
                                <FolderRounded />
                            )
                        ) : (
                            <ArticleIcon />
                        )}
                    </div>
                );
            }

            interface CustomLabelProps {
                children: string;
                className: string;
                label: string;
                type: FileOrFolder;
                path: string;
                tags: IndustryTag[];
                ragStatus: RagStatus;
            }

            function CustomLabel(props: CustomLabelProps) {
                const { children, className, type, tags } = props;
                const uploadPath = props.path.replace(/^knowledge\/?/, '');
                const folderRender = (uploadFolder: string) => {
                    return (
                        <>
                            <StorageUpload
                                key="upload"
                                display={`Upload`}
                                storageTarget={{
                                    type: 'knowledge_base',
                                    folder: uploadFolder
                                }}
                                toasterMessage="Uploading Knowledge: %name%"
                                onUpload={fileName => {
                                    refreshFolder();
                                }}
                            ></StorageUpload>
                            <Button
                                key="create-folder"
                                variant="light"
                                onClick={() => {
                                    onCreateFolder({
                                        type: 'knowledge_base',
                                        folder: uploadFolder
                                    });
                                }}
                            >
                                + Folder
                            </Button>
                            {uploadFolder !== '' && (
                                <Button
                                    key="delete-folder"
                                    variant="danger"
                                    onClick={() => {
                                        onDeleteFolder({
                                            type: 'knowledge_base',
                                            folder: uploadFolder
                                        });
                                    }}
                                >
                                    Delete
                                </Button>
                            )}
                        </>
                    );
                };

                const renderFile = (
                    fileTags: IndustryTag[],
                    ragStatus: RagStatus
                ) => {
                    const tagsEl = tags.map(tag => {
                        const color =
                            tag.value.indexOf('::') == -1
                                ? 'primary'
                                : 'secondary';
                        const name = tag.value.split('::');

                        return (
                            <Chip
                                key={`chip_${tag.value}`}
                                label={name[name.length - 1]}
                                color={color}
                            />
                        );
                    });

                    const ragStatusEl =
                        props.ragStatus == 'completed' ? (
                            <DoneIcon key="done-icon" color="success" />
                        ) : (
                            <ClearIcon key="clear-icon" color="error" />
                        );

                    const deleteButton = (
                        <Button
                            key="delete-folder"
                            variant="danger"
                            onClick={() => {
                                onDeleteFile(props.path);
                            }}
                        >
                            Delete
                        </Button>
                    );
                    return (
                        <>
                            {tagsEl}
                            {ragStatusEl}
                            {deleteButton}
                        </>
                    );
                };

                return (
                    <div className={className}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography>{children}</Typography>
                            {type === 'folder' && folderRender(uploadPath)}
                            {type === 'file' &&
                                renderFile(tags, props.ragStatus)}
                            {/* {type === 'folder' && (
                                <Button variant="light">+ Folder</Button>
                            )} */}
                        </Stack>
                    </div>
                );
            }
            const CustomTreeItem = forwardRef(function CustomTreeItem(
                props: TreeItemProps,
                ref: React.Ref<HTMLLIElement>
            ) {
                const { status } = useTreeItem(props);
                const item = useTreeItemModel<TreeEntry>(props.itemId)!;
                return (
                    <TreeItem
                        {...props}
                        ref={ref}
                        slots={{
                            label: CustomLabel,
                            iconContainer: CustomIcon
                        }}
                        slotProps={{
                            label: {
                                label: item.type,
                                type: item.type,
                                path: item.path,
                                tags: item.tags,
                                ragStatus: item.ragStatus
                            } as CustomLabelProps,
                            iconContainer: {
                                isOpen: status.expanded,
                                type: item.type
                            } as CustomIconProps
                        }}
                    />
                );
            });

            return (
                <RichTreeView
                    expandedItems={expandedTree}
                    checkboxSelection={true}
                    onExpandedItemsChange={handleToggleTree}
                    multiSelect={true}
                    selectionPropagation={{
                        descendants: true,
                        parents: false
                    }}
                    onSelectedItemsChange={(e, ids) => {
                        setSelectedIds(ids);
                    }}
                    items={treeItems}
                    slots={{ item: CustomTreeItem }}
                />
            );
        }
    )
);
